import { useCallback, useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	FormControlLabel,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	Paper,
	Snackbar,
	Switch,
	Typography,
} from "@mui/material";
import {
	CloudUpload as UploadIcon,
	Delete as DeleteIcon,
	PictureAsPdf as PdfIcon,
	OpenInNew as OpenIcon,
	DragIndicator as DragIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { EmailAttachmentPayload } from "../dialogs/EmailForwardRefDialog";

export type PDFAttachment = {
	extension: string;
	id: string;
	name: string;
	size: number;
	blob: Blob;
	isExisting: boolean;
	url?: string;
};

export type PDFAttachmentsProps = {
	existingAttachments: PDFAttachment[];
	onAttachmentsChange: (attachments: PDFAttachment[], shouldMerge: boolean) => void;
	maxFileSize?: number;
	allowMerge?: boolean;
};

const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const generateId = (): string => {
	return `pdf-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Converts a base64 encoded string to a Blob object
 * @param base64String - The base64 encoded string (with or without data URL prefix)
 * @param contentType - MIME type for the blob (default: 'application/pdf')
 */
export const base64ToBlob = (base64String: string, contentType = "application/pdf"): Blob => {
	// Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
	const base64Data = base64String.includes(",") ? base64String.split(",")[1] : base64String;

	const byteCharacters = atob(base64Data);
	const byteNumbers = new Array(byteCharacters.length);

	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}

	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: contentType });
};

/**
 * Converts an array of base64 encoded strings to PDFAttachment objects
 * @param attachmentStrings - Array of base64 encoded PDF strings from backend
 * @param extension - File extension ('.pdf'), defaults to '.pdf'
 * @returns Array of PDFAttachment objects with generated URLs
 */
export const initializeExistingAttachments = (
	attachmentStrings: EmailAttachmentPayload[],
): PDFAttachment[] => {
	return attachmentStrings.map((base64String, index) => {
		const contentType = "application/pdf";
		const extension = base64String.name.substring(base64String.name.lastIndexOf(".")) || ".pdf";
		const blob = base64ToBlob(base64String.content, contentType);
		const url = URL.createObjectURL(blob);

		return {
			id: `existing-${Date.now()}-${index}`,
			name: base64String.name,
			size: blob.size,
			blob: blob,
			isExisting: true,
			url,
			extension,
		};
	});
};

/**
 * Converts a Blob to a base64 encoded string (without data URL prefix)
 * @param blob - The Blob to convert
 * @returns Promise resolving to base64 string
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			// Result is a data URL, extract only the base64 part
			const base64 = (reader.result as string).split(",")[1];
			resolve(base64);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
};

const dropZoneBaseStyles = {
	border: "2px dashed",
	borderRadius: 2,
	padding: 3,
	textAlign: "center" as const,
	cursor: "pointer",
	transition: "all 0.2s ease-in-out",
	backgroundColor: "background.paper",
};

export const PDFAttachments = ({
	existingAttachments,
	onAttachmentsChange,
	maxFileSize = 25 * 1024 * 1024, // 25MB default
	allowMerge = true,
}: PDFAttachmentsProps) => {
	const { t } = useTranslation();

	const [newAttachments, setNewAttachments] = useState<PDFAttachment[]>([]);

	const [shouldMergePDFs, setShouldMergePDFs] = useState(false);

	const [isDragOver, setIsDragOver] = useState(false);

	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [showError, setShowError] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const createdUrlsRef = useRef<string[]>([]);

	// Drag and drop reordering state
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
	const [orderedAttachments, setOrderedAttachments] = useState<PDFAttachment[]>([]);

	// Initialize ordered attachments when existing or new attachments change
	useEffect(() => {
		const combined = [...existingAttachments, ...newAttachments];
		setOrderedAttachments(combined);
	}, [existingAttachments, newAttachments]);

	useEffect(() => {
		onAttachmentsChange(orderedAttachments, shouldMergePDFs);
	}, [orderedAttachments, shouldMergePDFs]);

	useEffect(() => {
		return () => {
			createdUrlsRef.current.forEach((url) => {
				URL.revokeObjectURL(url);
			});
		};
	}, []);

	const validateFile = useCallback(
		(file: File): { valid: boolean; error?: string } => {
			const isPDF = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

			if (!isPDF) {
				return {
					valid: false,
					error: t("only-pdf-allowed"),
				};
			}

			if (file.size > maxFileSize) {
				const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
				return {
					valid: false,
					error: t("file-too-large") + `. ${t("max-size")}: ${maxSizeMB}MB`,
				};
			}

			return { valid: true };
		},
		[maxFileSize, t],
	);

	const processFiles = useCallback(
		(files: FileList | File[]) => {
			const fileArray = Array.from(files);
			const validAttachments: PDFAttachment[] = [];
			const errors: string[] = [];

			fileArray.forEach((file) => {
				const validation = validateFile(file);

				if (!validation.valid) {
					errors.push(`${file.name}: ${validation.error}`);
					return;
				}

				const url = URL.createObjectURL(file);
				createdUrlsRef.current.push(url);

				const attachment: PDFAttachment = {
					id: generateId(),
					name: file.name,
					size: file.size,
					blob: file,
					isExisting: false,
					url,
					extension: file.name.substring(file.name.lastIndexOf(".")) || ".pdf",
				};

				validAttachments.push(attachment);
			});

			if (errors.length > 0) {
				setErrorMessage(errors.join("\n"));
				setShowError(true);
			}

			if (validAttachments.length > 0) {
				setNewAttachments((prev) => [...prev, ...validAttachments]);
			}
		},
		[validateFile],
	);

	const handleDelete = useCallback((attachmentId: string) => {
		// Find and clean up URL for the attachment being deleted
		const attachment = orderedAttachments.find((a) => a.id === attachmentId);
		if (attachment?.url && !attachment.isExisting) {
			URL.revokeObjectURL(attachment.url);
			createdUrlsRef.current = createdUrlsRef.current.filter((u) => u !== attachment.url);
		}

		// Remove from newAttachments state
		setNewAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
	}, [orderedAttachments]);

	const handlePreview = useCallback((attachment: PDFAttachment) => {
		if (attachment.url) {
			window.open(attachment.url, "_blank", "noopener,noreferrer");
		}
	}, []);

	const handleDragEnter = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragOver(false);

			const files = e.dataTransfer.files;
			if (files.length > 0) {
				processFiles(files);
			}
		},
		[processFiles],
	);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			if (files && files.length > 0) {
				processFiles(files);
			}
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		},
		[processFiles],
	);

	const handleDropZoneClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			fileInputRef.current?.click();
		}
	}, []);

	// Drag and drop reordering handlers
	const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
		// Prevent dragging the first existing attachment
		if (index === 0 && orderedAttachments[0]?.isExisting) {
			e.preventDefault();
			return;
		}
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = "move";
	}, [orderedAttachments]);

	const handleDragEnd = useCallback(() => {
		setDraggedIndex(null);
		setDragOverIndex(null);
	}, []);

	const handleDragEnterItem = useCallback((e: React.DragEvent, index: number) => {
		e.preventDefault();
		e.stopPropagation();
		// Prevent dropping before the first existing attachment
		if (index === 0 && orderedAttachments[0]?.isExisting) {
			return;
		}
		setDragOverIndex(index);
	}, [orderedAttachments]);

	const handleDragOverItem = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	}, []);

	const handleDropItem = useCallback((e: React.DragEvent, dropIndex: number) => {
		e.preventDefault();
		e.stopPropagation();

		if (draggedIndex === null || draggedIndex === dropIndex) {
			return;
		}

		// Prevent dropping before the first existing attachment
		if (dropIndex === 0 && orderedAttachments[0]?.isExisting) {
			return;
		}

		const newOrder = [...orderedAttachments];
		const [draggedItem] = newOrder.splice(draggedIndex, 1);
		newOrder.splice(dropIndex, 0, draggedItem);

		setOrderedAttachments(newOrder);
		setDraggedIndex(null);
		setDragOverIndex(null);
	}, [draggedIndex, orderedAttachments]);

	return (
		<Box>
			<input
				ref={fileInputRef}
				type="file"
				accept="application/pdf,.pdf"
				multiple
				onChange={handleFileInputChange}
				style={{ display: "none" }}
			/>

			<Snackbar
				open={showError}
				autoHideDuration={6000}
				onClose={() => setShowError(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					onClose={() => setShowError(false)}
					severity="error"
					variant="filled"
					sx={{ width: "100%", whiteSpace: "pre-line" }}>
					{errorMessage}
				</Alert>
			</Snackbar>

			{orderedAttachments.length > 0 && (
				<List sx={{ mt: 2 }}>
					{orderedAttachments.map((attachment, index) => {
						const isFirstExisting = index === 0 && attachment.isExisting;
						const isDraggable = !isFirstExisting;
						const isDragging = draggedIndex === index;
						const isDropTarget = dragOverIndex === index;

						return (
							<ListItem
								key={attachment.id}
								draggable={isDraggable}
								onDragStart={(e) => handleDragStart(e, index)}
								onDragEnd={handleDragEnd}
								onDragEnter={(e) => handleDragEnterItem(e, index)}
								onDragOver={handleDragOverItem}
								onDrop={(e) => handleDropItem(e, index)}
								sx={{
									backgroundColor: "background.paper",
									borderRadius: 1,
									mb: 1,
									border: "2px solid",
									borderColor: isDropTarget ? "primary.main" : "divider",
									opacity: isDragging ? 0.5 : attachment.isExisting ? 0.8 : 1,
									cursor: isDraggable ? "grab" : "default",
									transition: "all 0.2s ease",
									"&:active": {
										cursor: isDraggable ? "grabbing" : "default",
									},
								}}>
								{isDraggable && (
									<ListItemIcon sx={{ minWidth: 32 }}>
										<DragIcon sx={{ color: "text.secondary", fontSize: 20 }} />
									</ListItemIcon>
								)}
								<ListItemIcon sx={{ minWidth: 40 }}>
									{attachment.extension.toLowerCase() === ".pdf" ? (
										<PdfIcon color="error" sx={{ fontSize: 32 }} />
									) : (
										""
									)}
								</ListItemIcon>
								<ListItemText
									primary={
										<Typography
											variant="body2"
											sx={{
												fontWeight: 500,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}>
											{attachment.name}
											{attachment.isExisting && (
												<Typography
													component="span"
													variant="caption"
													sx={{ ml: 1, color: "text.secondary" }}>
													({t("existing")})
												</Typography>
											)}
										</Typography>
									}
									secondary={formatFileSize(attachment.size)}
								/>
								<ListItemSecondaryAction>
									<IconButton
										edge="end"
										title={t("preview")}
										onClick={() => handlePreview(attachment)}
										sx={{ mr: attachment.isExisting ? 0 : 1 }}>
										<OpenIcon />
									</IconButton>

									{!attachment.isExisting && (
										<IconButton
											edge="end"
											aria-label={t("delete")}
											onClick={() => handleDelete(attachment.id)}
											color="error">
											<DeleteIcon />
										</IconButton>
									)}
								</ListItemSecondaryAction>
							</ListItem>
						);
					})}
				</List>
			)}

			{allowMerge &&
				orderedAttachments.filter((att) => att.extension.toLowerCase() === ".pdf").length > 1 && (
					<FormControlLabel
						control={
							<Switch
								checked={shouldMergePDFs}
								onChange={(e) => setShouldMergePDFs(e.target.checked)}
								color="primary"
							/>
						}
						label={t("merge-pdfs")}
						sx={{ mb: 4 }}
					/>
				)}

			<Paper
				elevation={0}
				onClick={handleDropZoneClick}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				role="button"
				sx={{
					...dropZoneBaseStyles,
					borderColor: isDragOver ? "primary.main" : "divider",
					backgroundColor: isDragOver ? "action.hover" : "background.paper",
					"&:hover": {
						borderColor: "primary.main",
						backgroundColor: "action.hover",
					},
					"&:focus": {
						outline: "2px solid",
						outlineColor: "primary.main",
						outlineOffset: 2,
					},
				}}>
				<UploadIcon sx={{ fontSize: 48, color: isDragOver ? "primary.main" : "text.secondary" }} />
				<Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
					{t("drag-pdf-files-here")}
				</Typography>
				<Typography variant="body2" color="text.disabled">
					{t("or-click-to-select-files")}
				</Typography>
				<Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
					{t("max-size") + `: ${Math.round(maxFileSize / (1024 * 1024))}MB`}
				</Typography>
			</Paper>
		</Box>
	);
};

export default PDFAttachments;
